<template>
  <ModalView :isVisible="isModalVisible" :title="'Payment'" @close="closeModal">
    <div class="payment-container-row">
      <div class="header-payment-row">
        <div>
          <img v-if="product?.images?.length" :src="product.images[0].urlDetailed"
            :alt="product.product_name || product.title" />
          <img v-else-if="product?.thumbnail_image?.length" :src="product.thumbnail_image[0].url"
            :alt="product.product_name || product.title" />
        </div>
        <div>
          <p v-if="contactDetails.mode == 'subscription'">
            Subscribe to {{ product.product_name ? product.product_name : product.title }} and 1 more
          </p>
          <div v-else>
            <p class="product-text">{{ product.product_name ? product.product_name : product.title }}</p>
            <p class="product-title">
              You are purchasing a {{ product.product_name }} for {{ store.getters.profile.profile.name }} – {{
                store.getters.profile.profile.barcode }}
            </p>
          </div>

          <!-- Subscription details -->
          <div class="payment-flex-row" v-if="contactDetails.mode == 'subscription'">
            <div>
              <p>{{ product.product_name ? product.product_name : product.title }}</p>
              <p>Every {{ contactDetails.interval }} Month</p>
            </div>
            <div class="d-flex">
              <p>{{ subscription }}</p>
            </div>
          </div>
          <div class="payment-flex-row" v-if="contactDetails.mode == 'subscription'">
            <div>
              <p>Do it Yourself (Fingerprick)</p>
              <p>Every {{ contactDetails.interval }} Month</p>
            </div>
            <div>
              <p>£0.00</p>
            </div>
          </div>
        </div>

        <div class="third-price" v-if="contactDetails.mode == 'subscription'">
          <p>{{ subscription }}</p>
          <p class="product-title">Every {{ contactDetails.interval }} Month</p>
        </div>
        <div class="third-price" v-else>
          <p>{{ productPrice }}</p>
        </div>
      </div>

      <!-- Warning messages -->
      <div class="alert alert-danger mt-4" role="alert"
        v-if="hasNoneLdnaTest(primaryVariant?.sku, store.getters.profile.profile.barcode)">
        The number of traits (up to 14) you receive is dependent
        on the genetic markers and quality of data generated
        from your original testing provider. Living DNA cannot
        guarantee the quality of other providers' data and
        therefore cannot guarantee the number of traits you will
        receive.
      </div>
      <div v-else-if="product?.warningMessage" v-html="product?.warningMessage" class="alert alert-warning mt-4"
        role="alert" />

      <!-- Contact info -->
      <div v-if="isShippable" class="contact-info-row">
        <div class="contact-info-grid-row">
          <div>
            <p>Contact</p>
            <p>{{ contactDetails.phone }}</p>
          </div>
          <div>
            <p @click="change">Change</p>
          </div>
        </div>

        <div class="contact-info-grid-row">
          <div class="mt-3">
            <p>Ship to</p>
            <p>
              {{ contactDetails.firstName }} {{ contactDetails.lastName }}<br />
              {{ contactDetails.lineOne }}<br />
              {{ contactDetails.city }}{{ contactDetails.state ? ', ' + contactDetails.state : '' }}
              {{contactDetails.zip }}<br />
              {{ country?.name }}
            </p>
          </div>
          <div>
            <p @click="change">Change</p>
          </div>
        </div>
      </div>

      <h4 v-if="isShippable">Shipping Options:</h4>

      <div v-if="isShippable && shippingMethods.length"
        class="pricing-container">
        <div v-for="(method, index) in shippingMethods" :key="index" class="subcription-price-row">
          <div>
            <label class="custom-radio">
                <input
                    type="radio"
                    name="shipping"
                    :id="'shippingOption' + index"
                    v-model="selectedShippingMethod"
                    @change="changeMethod()"
                    :value="shippingValue(method)"
                    :disabled="!isKit && !EXPECTED_SHIPPING_SLUGS.has(shippingValue(method))"
                />
              <span class="checkmark"></span>
            </label>
            <p>{{ method.name }}</p>
          </div>
          <div>
            <p>{{ convertToCurrency(method.shippingPrice / 100, primaryVariant?.currency) }}</p>
          </div>
        </div>
      </div>

      <!-- Shipping could not be resolved for a shippable item — degrade gracefully
           instead of a silent empty state / confusing generic error on pay. -->
      <div v-if="isShippable && !shippingMethods.length"
        class="shipping-unavailable-notice"
        style="margin: 8px 0 4px; padding: 10px 12px; border-radius: 8px; background: #FDECEA; color: #B71C1C; font-size: 14px; text-align: left;">
        <p style="margin: 0;" v-if="shippingUnavailable">
          We couldn't load shipping options for this item right now. Please try again shortly.
        </p>
        <p style="margin: 0;" v-else>
          No shipping options are available for this item in your region.
        </p>
      </div>

      <!-- Action buttons -->
      <div class="action-cta">
        <!-- Apple Pay / Google Pay: shown only when the browser + Stripe account support a wallet. -->
        <div v-show="walletAvailable" class="wallet-section">
          <div id="pr-button-modal"></div>
          <div class="wallet-divider"><span>or</span></div>
        </div>
        <p v-if="walletError" class="wallet-error">{{ walletError }}</p>
        <!-- Wrapped: BloodTestDetails.scss colours .action-cta's direct buttons by position, which
             made this one green -->
        <div class="pay-by-card">
          <button class="ldna-btn" @click="openPaymentCardModal" :disabled="isShippable && !shippingMethods.length">Pay by card</button>
        </div>
        <div id="paypal-button-container" style="position: relative;">
          <div ref="paypalButtonsHost"></div>
          <div v-if="isShippable && !shippingMethods.length"
            class="paypal-shipping-block"
            style="position: absolute; inset: 0; background: rgba(255,255,255,0.55); cursor: not-allowed;"
            title="Shipping unavailable — please try again shortly"></div>
          <div v-if="paypalProcessing" class="paypal-processing-overlay">
            <div class="spinner"></div>
            <p>Processing payment...</p>
          </div>
        </div>
      </div>
    </div>
  </ModalView>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue';
import ModalView from "@/components/Modal/ModalView.vue";
import { defineEmits, defineProps } from 'vue';
import store from '@/store';
import { getPrice, getSubscriptionPrice } from "@/services/price.js";
import OrderService from '@/services/order';
import ProductService, { CreateStripeOrder } from '@/services/product';
import { loadStripe } from '@stripe/stripe-js';
import countries from "@/countries.js";

const props = defineProps({
  contactDetails: {
    type: Object,
    default: () => ({})
  },
  product: {
    type: Object,
    default: () => ({})
  },
  isModalVisible: {
    type: Boolean,
    default: false
  },
  barcode: {
    type: String,
    default: null
  }
});

const effectiveBarcode = computed(() => props.barcode || store.getters.profile.profile.barcode);

const emit = defineEmits([
  'close',
  'closePaymentPaypalModal',
  'openPaymentCardModal',
  'back',
  'openCheckoutModal',
  'order'
]);

// Reactive state
const shippingMethods = ref([]);
const shippingPrices = ref(null);
// True when a shipping lookup errored (e.g. the CMS shipping endpoint 5xx'd) rather
// than legitimately returning no methods — lets us show a clear "try again" message.
const shippingUnavailable = ref(false);
const selectedShippingMethod = ref(null);
const paypalButtonRendered = ref(false);
const processingOrder = ref(false);
const paypalProcessing = ref(false);
const processedOrderIds = ref(new Set());
const paypalButtonsHost = ref(null);
const paypalButtonsInstance = ref(null);
let paypalScriptPromise = null;

// --- Apple Pay / Google Pay (Stripe Payment Request Button) ---
const stripeInstance = ref(null);
const paymentRequest = ref(null);
const paymentRequestButton = ref(null);
const walletAvailable = ref(false);
const walletError = ref('');
const regionSkuKeyByCode = {
  GBR: "sku",
  USA: "usa_sku",
  AUS: "aus_sku",
  EUR: "eur_sku",
  CAN: "can_sku",
  INT: "sku",
};
const regionPriceKeyByCode = {
  GBR: "price",
  USA: "usa_price",
  AUS: "aus_price",
  EUR: "eur_price",
  CAN: "can_price",
  INT: "price",
};
const currencyByRegion = {
  GBR: "GBP",
  USA: "USD",
  AUS: "AUD",
  EUR: "EUR",
  CAN: "CAD",
  INT: "USD",
};
const symbolByCurrency = {
  GBP: "£",
  USD: "$",
  AUD: "A$",
  EUR: "€",
  CAD: "C$",
};
const EXPECTED_SHIPPING_SLUGS = new Set([
  "digitalcollection",
  "cliniccollection",
  "selfcollection",
  "selfcollectionsaliva",
]);
const normalizeText = (value) => String(value || "").trim().toLowerCase();

const buildShippingMethodsFromPreprCollectionMethods = () => {
  const methods = Array.isArray(props.product?.collection_methods) ? props.product.collection_methods : [];
  const hydrated = methods.map((method) => ({
    name: method?.name || "",
    slug: normalizeText(method?._slug || method?.slug),
    _slug: method?._slug,
    shippingPrice: Number(method?.price || 0),
    currency: primaryVariant.value?.currency || "GBP",
    source: "prepr_collection_methods",
  }));
  const invalid = hydrated.filter((method) => !method.slug || !EXPECTED_SHIPPING_SLUGS.has(method.slug));
  if (invalid.length > 0) {
    console.error("[Checkout Shipping] Invalid or missing Prepr collection method slug(s).", {
      invalidCollectionMethods: invalid.map((m) => ({ name: m.name, slug: m.slug, _slug: m._slug })),
      expectedSlugs: Array.from(EXPECTED_SHIPPING_SLUGS),
    });
  }
  return hydrated;
};

const normalizeValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim().toLowerCase() === "null") return null;
  return value;
};
const requiresShipping = computed(() => {
  const productType = props.product?.product_type || "";
  const categories = props.product?.categories || [];
  if (productType === "Blood Test") return true;
  if (productType === "Genetic Test") return false;
  if (Array.isArray(categories)) {
    const lowered = categories.map((c) => String(c).toLowerCase());
    if (lowered.includes("digital upgrade")) return false;
  }
  return !!props.product?.variants?.[0]?.shippable;
});
const isGeneticTest = computed(
  () => String(props.product?.product_type || "").trim().toLowerCase() === "genetic test"
);
const hasCollectionMethods = computed(() =>
  Array.isArray(props.product?.collection_methods) && props.product.collection_methods.length > 0
);

// A product is treated as a "Kit" (API-based shipping) when it is shippable but has no Prepr
// collection_methods. Ancestry/member kits come from Craft CMS and have no collection_methods;
// health products (Blood/Genetic Tests) come from Prepr and always carry collection_methods.
const isKit = computed(() => {
  if (hasCollectionMethods.value) return false;
  const categories = props.product?.categories;
  if (typeof categories === 'string') return categories === 'Kit';
  if (Array.isArray(categories)) return categories.includes('Kit');
  // Shippable product with no collection_methods → treat as a physical kit
  return requiresShipping.value;
});
const selectedRegion = computed(() => {
  const selectedCountry = countries.find(item => item.code === props.contactDetails?.countryCode);
  return selectedCountry?.region || store.getters?.profile?.profile?.region || "GBR";
});
const fallbackVariant = computed(() => {
  const region = selectedRegion.value;
  const skuKey = regionSkuKeyByCode[region] || "sku";
  const priceKey = regionPriceKeyByCode[region] || "price";
  const resolvedSku = normalizeValue(props.product?.[skuKey]) || normalizeValue(props.product?.sku);

  if (!resolvedSku) {
    return null;
  }

  const resolvedCurrency = currencyByRegion[region] || props.product?.currency || "USD";
  const resolvedPrice = Number(props.product?.[priceKey] ?? props.product?.price ?? 0);

  return {
    sku: resolvedSku,
    currency: resolvedCurrency,
    shippable: requiresShipping.value,
    price: {
      base: resolvedPrice,
      isOnSale: false,
    },
    currencySymbol: {
      raw: symbolByCurrency[resolvedCurrency] || "",
    },
  };
});
const primaryVariant = computed(() => props.product?.variants?.[0] || fallbackVariant.value);
const isShippable = computed(() => requiresShipping.value && !!primaryVariant.value?.shippable);

const isSubscription = computed(() =>
  props.contactDetails?.mode === 'subscription' && !!props.product?.subscription_price
);
const country = computed(() => countries.find(item => item.code === props.contactDetails?.countryCode));
const currency = computed(() => {
  if (primaryVariant.value?.currency) {
    return primaryVariant.value.currency;
  }
  return getCurrencySymbol(props.product?.subscription_price_string || props.product?.price_string);
});

const productPrice = computed(() => {
  if (!props.product) return 0;
  if (getPrice(props.product) === undefined && props.product.variants) {
    if (primaryVariant.value?.price?.isOnSale) {
      return primaryVariant.value.currencySymbol.raw + primaryVariant.value.price.sale / 100;
    }
    if (primaryVariant.value?.price?.base) {
      return primaryVariant.value.currencySymbol.raw + primaryVariant.value.price.base / 100;
    }
    return 0;
  } else {
    return getPrice(props.product);
  }
});

const subscription = computed(() => {
  return props.product ? getSubscriptionPrice(props.product) : undefined;
});

const totalPrice = computed(() => {
  let price = 0;

  if (primaryVariant.value?.price?.base) {
    price = Number(primaryVariant.value.price.base / 100);
  } else if (props.product?.subscription_price) {
    price = props.product.subscription_price / 100;
  }

  // Add shipping cost if applicable
  if (shippingPrices.value) {
    price += Number(shippingPrices.value);
  }

  // FIX: Round to exactly 2 decimal places to avoid PayPal DECIMAL_PRECISION error
  return formatToTwoDecimals(price);
});

// --- Apple Pay / Google Pay ---------------------------------------------------
const walletCurrency = () =>
  (primaryVariant.value?.currency || currencyByRegion[selectedRegion.value] || 'GBP').toLowerCase();

const walletAmountCents = () => Math.round(Number(totalPrice.value || 0) * 100);

const teardownWallet = () => {
  if (paymentRequestButton.value) {
    try { paymentRequestButton.value.unmount(); } catch (e) { /* already unmounted */ }
    paymentRequestButton.value = null;
  }
  paymentRequest.value = null;
  walletAvailable.value = false;
};

// Build the Stripe Payment Request and, if the browser + Stripe account support a
// wallet, render the button. Reuses the same PaymentIntent flow as the card path.
const mountWalletButton = async () => {
  teardownWallet();
  walletError.value = '';

  const amount = walletAmountCents();
  if (!amount || amount <= 0) return; // total not ready yet (e.g. shipping still loading)

  if (!stripeInstance.value) {
    stripeInstance.value = await loadStripe(process.env.VUE_APP_STRIPE_ID);
  }
  if (!stripeInstance.value) return;

  paymentRequest.value = stripeInstance.value.paymentRequest({
    country: 'GB',
    currency: walletCurrency(),
    total: { label: 'Living DNA', amount },
    requestPayerName: true,
    requestPayerEmail: true,
  });

  const canPay = await paymentRequest.value.canMakePayment();
  // Diagnostic: null => no wallet in this browser OR Apple/Google Pay not enabled in
  // the Stripe account; { applePay: true } / { googlePay: true } => available.
  console.log('[Wallet] canMakePayment result:', canPay);
  if (!canPay) {
    walletAvailable.value = false;
    return;
  }

  const elements = stripeInstance.value.elements();
  paymentRequestButton.value = elements.create('paymentRequestButton', {
    paymentRequest: paymentRequest.value,
    style: { paymentRequestButton: { type: 'default', theme: 'dark', height: '48px' } },
  });
  walletAvailable.value = true;
  await nextTick();
  if (document.getElementById('pr-button-modal')) {
    paymentRequestButton.value.mount('#pr-button-modal');
  }

  paymentRequest.value.on('paymentmethod', async (ev) => {
    try {
      const order = new CreateStripeOrder(
        effectiveBarcode.value,
        'AX',
        props.contactDetails,
        selectedShippingMethod.value,
        props.product,
        walletAmountCents()
      );
      const data = await ProductService.createPaymentIntentForOrder(order);
      if (!data?.paymentIntentclientSecret) {
        ev.complete('fail');
        walletError.value = 'Could not start the payment. Please try again.';
        return;
      }

      // Confirm without letting Stripe handle next actions inside the wallet sheet.
      const { error: confirmError, paymentIntent } = await stripeInstance.value.confirmCardPayment(
        data.paymentIntentclientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false }
      );

      if (confirmError) {
        ev.complete('fail');
        walletError.value = confirmError.message;
        return;
      }

      ev.complete('success');

      // Finish any required next action (e.g. 3-D Secure) after the sheet closes.
      if (paymentIntent.status === 'requires_action') {
        const actioned = await stripeInstance.value.confirmCardPayment(data.paymentIntentclientSecret);
        if (actioned.error) {
          walletError.value = actioned.error.message;
          return;
        }
      }

      emit('order', data);
      emit('closePaymentPaypalModal');
    } catch (e) {
      ev.complete('fail');
      walletError.value = e?.message || 'Payment failed. Please try again.';
    }
  });
};

// Mount when the modal opens; tear down when it closes.
watch(
  () => props.isModalVisible,
  (visible) => {
    if (visible) {
      nextTick(() => { mountWalletButton(); });
    } else {
      teardownWallet();
    }
  }
);

// Shipping selection changes the total after the sheet is built — keep it in sync,
// and mount late if the total only became valid after the modal opened.
watch(totalPrice, (val) => {
  const amount = Math.round(Number(val || 0) * 100);
  if (paymentRequest.value && amount > 0) {
    paymentRequest.value.update({ total: { label: 'Living DNA', amount } });
  } else if (!paymentRequest.value && amount > 0 && props.isModalVisible) {
    mountWalletButton();
  }
});

onBeforeUnmount(() => { teardownWallet(); });

// Methods
const getMethodSlug = (method) => {
  if (!method) return "";
  return String(method.slug || method._slug || "").trim().toLowerCase();
};

const logShippingDebug = (context, extra = {}) => {
  console.groupCollapsed(`[Checkout Shipping] ${context}`);
  console.log("selectedShippingMethod(actual):", selectedShippingMethod.value);
  console.log("expectedShippingSlugs:", Array.from(EXPECTED_SHIPPING_SLUGS));
  console.table(
    (shippingMethods.value || []).map((m) => ({
      name: m?.name,
      slug: m?.slug,
      _slug: m?._slug,
      resolvedSlug: getMethodSlug(m),
      isExpected: EXPECTED_SHIPPING_SLUGS.has(getMethodSlug(m)),
    }))
  );
  if (Object.keys(extra).length > 0) {
    console.log("extra:", extra);
  }
  console.groupEnd();
};

const ensureValidShippingSelection = () => {
  if (!isShippable.value) return true;
  const actual = String(selectedShippingMethod.value || "").trim().toLowerCase();

  if (isKit.value) {
    // Kit products use API shipping slugs which are not in EXPECTED_SHIPPING_SLUGS
    if (!actual) {
      console.error("[Checkout Shipping] Missing shipping selection for Kit product.");
      logShippingDebug("Missing Kit shipping selection");
      return false;
    }
    const availableSlugs = (shippingMethods.value || []).map((m) => getMethodSlug(m));
    if (!availableSlugs.includes(actual)) {
      console.error("[Checkout Shipping] Selected Kit shipping slug not in available methods.", { actual, availableSlugs });
      return false;
    }
    return true;
  }

  const availableExpected = (shippingMethods.value || [])
    .map((method) => getMethodSlug(method))
    .filter((slug) => EXPECTED_SHIPPING_SLUGS.has(slug));

  if (!actual) {
    console.error("[Checkout Shipping] Missing selected shipping slug.");
    logShippingDebug("Missing selected shipping slug");
    return false;
  }
  if (!EXPECTED_SHIPPING_SLUGS.has(actual)) {
    console.error("[Checkout Shipping] Invalid shipping slug.", {
      actual,
      expected: Array.from(EXPECTED_SHIPPING_SLUGS),
    });
    logShippingDebug("Invalid shipping slug", { actual });
    return false;
  }
  if (!availableExpected.includes(actual)) {
    console.error("[Checkout Shipping] Selected slug is not present in available shipping methods.", {
      actual,
      availableExpected,
    });
    logShippingDebug("Selected slug not available", { actual, availableExpected });
    return false;
  }
  return true;
};

const resolveShippingForCheckout = () => {
  if (isShippable.value) {
    if (!ensureValidShippingSelection()) {
      return null;
    }
    return selectedShippingMethod.value;
  }

  if (isGeneticTest.value) {
    const digitalCollection = (shippingMethods.value || []).find(
      (method) => getMethodSlug(method) === "digitalcollection"
    );
    if (!digitalCollection) {
      console.error("[Checkout Shipping] Genetic Test requires digitalcollection, but it was not found in Prepr collection_methods.");
      logShippingDebug("Missing digitalcollection for Genetic Test");
      return null;
    }
    return "digitalcollection";
  }

  return selectedShippingMethod.value || "";
};

const shippingValue = (method) => {
  return getMethodSlug(method);
};

const changeMethod = () => {
  const matchedShippingMethod = shippingMethods.value.find(
    method => shippingValue(method) === selectedShippingMethod.value
  );
  shippingPrices.value = matchedShippingMethod ? formatToTwoDecimals(matchedShippingMethod.shippingPrice / 100) : null;
};
const pickDefaultShipping = () => {
    if (!isShippable.value) return;
    if (selectedShippingMethod.value) return;

    if (isKit.value) {
        // Kit products use standard API shipping — pick the first available method
        const firstMethod = (shippingMethods.value || [])[0];
        selectedShippingMethod.value = firstMethod ? shippingValue(firstMethod) : null;
        changeMethod();
        return;
    }

    const firstValidSlug = (shippingMethods.value || [])
        .map((m) => shippingValue(m))
        .find((slug) => EXPECTED_SHIPPING_SLUGS.has(slug));

    selectedShippingMethod.value = firstValidSlug || null;
    changeMethod();

    if (!firstValidSlug) {
        console.error("[Checkout Shipping] No valid expected shipping slug found in shipping methods.");
        logShippingDebug("No valid expected shipping slug found");
    }
};
const fetchShippingOptions = async () => {
    shippingUnavailable.value = false;
    try {
        if (isKit.value && isShippable.value && primaryVariant.value?.sku && props.contactDetails?.countryCode) {
            // Kit products require standard API shipping options (not Prepr collection methods)
            const apiMethods = await OrderService.getShippingOptions(
                primaryVariant.value.sku,
                props.contactDetails.countryCode
            );
            shippingMethods.value = (apiMethods || []).map(method => ({
                ...method,
                slug: normalizeText(method.slug || method.name || ""),
                source: "api_shipping",
            }));
        } else {
            shippingMethods.value = buildShippingMethodsFromPreprCollectionMethods();
        }

        if (!isShippable.value) {
            shippingPrices.value = null;
            selectedShippingMethod.value = null;
        } else {
            pickDefaultShipping();
        }

        logShippingDebug("Fetched shipping methods");
    } catch (error) {
        console.error("[Checkout Shipping] Error building shipping methods:", error);
        shippingMethods.value = [];
        shippingPrices.value = null;
        selectedShippingMethod.value = null;
        shippingUnavailable.value = true;
        logShippingDebug("Failed to build shipping methods");
    }
};
const closeModal = () => {
  emit('close');
};

const change = () => {
  emit("openCheckoutModal", props.contactDetails);
};

const openPaymentCardModal = () => {
  const shippingForCheckout = resolveShippingForCheckout();
  if (shippingForCheckout === null) {
    return;
  }
  const updatedContactDetails = {
    ...props.contactDetails,
    shipping: shippingForCheckout,
    price: shippingPrices.value
  };

  emit('openPaymentCardModal', updatedContactDetails);
  emit('close');
};

const convertToCurrency = (value, currencyType = 'USD') => {
  if (value === 0 || value === null || value === undefined) {
    return 'FREE';
  }

  const numericValue = Number(value);

  if (isNaN(numericValue)) {
    return 'INVALID';
  }

  return numericValue.toLocaleString(undefined, {
    style: 'currency',
    currency: currencyType,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

function getCurrencySymbol(amountString) {
  if (!amountString) return 'USD';
  const currencySymbol = amountString.match(/[^\d.,\s]+/g);
  return currencySymbol && currencySymbol[0] === '£' ? 'GBP' : 'USD';
}

const hasNoneLdnaTest = (sku, barcode) => {
  return !!(sku && sku.match("LDNA-WELLBEING-UPGRADE") && barcode && barcode.match("LT"));
};

// FIX: Helper function to format to exactly 2 decimal places
// Function declaration (hoisted) so computeds/watchers evaluated earlier in setup —
// e.g. the wallet total watcher reading totalPrice — can call it without a TDZ error.
function formatToTwoDecimals(value) {
  if (value === null || value === undefined) return null;

  // Use Number.EPSILON to handle floating point precision issues
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

// FIX: Helper function to format as string with exactly 2 decimal places for PayPal
const formatPayPalAmount = (value) => {
  if (value === null || value === undefined) return '0.00';

  const formatted = formatToTwoDecimals(value);
  // Return as string with exactly 2 decimal places
  return formatted.toFixed(2);
};

// Store processed orders in localStorage for persistence across page reloads
const getStoredOrderIds = () => {
  try {
    const stored = localStorage.getItem('processedPaypalOrders');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

const storeOrderId = (orderId) => {
  processedOrderIds.value.add(orderId);
  try {
    const stored = Array.from(processedOrderIds.value);
    localStorage.setItem('processedPaypalOrders', JSON.stringify(stored));
  } catch {
    // Ignore localStorage errors
  }
};

// Function to generate correlation ID
const generateCorrelationId = () => {
  return `paypal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Simple function to close PayPal popups
const closePayPalPopups = () => {
  // Intentionally no direct DOM manipulation here.
  // Removing PayPal/Vue nodes imperatively can break Vue's patch cycle.
};

const cleanupPayPalButtons = () => {
  if (paypalButtonsInstance.value && typeof paypalButtonsInstance.value.close === 'function') {
    try {
      paypalButtonsInstance.value.close();
    } catch (e) {
      console.warn('Error closing PayPal button instance:', e);
    }
  }
  paypalButtonsInstance.value = null;
  paypalButtonRendered.value = false;
  if (paypalButtonsHost.value) {
    paypalButtonsHost.value.innerHTML = '';
  }
};

const logCheckoutErrorDetails = (error, context = 'checkout') => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const url = error?.config?.url;
  const method = error?.config?.method;
  const headers = error?.response?.headers;

  console.error(`[${context}] request failed`, {
    status,
    method,
    url,
    data,
    headers,
    message: error?.message,
    code: error?.code,
  });
  try {
    console.error(`[${context}] response data json`, JSON.stringify(data));
  } catch (e) {
    console.error(`[${context}] response data raw`, data);
  }
  console.error(`[${context}] status/url`, status, method, url);
};

// Remove the loaded PayPal SDK so it can be re-loaded with a different currency/intent.
const unloadPayPalScript = () => {
  cleanupPayPalButtons();
  document
    .querySelectorAll('#paypal-script, script[src*="paypal.com/sdk/js"]')
    .forEach((s) => s.remove());
  try {
    delete window.paypal;
  } catch (e) {
    window.paypal = undefined;
  }
  // Tracked on window (not component state) because window.paypal persists across
  // modal remounts and we must know which currency/intent it was loaded with.
  window.__paypalSdkKey = null;
  paypalScriptPromise = null;
};

// PayPal integration
const loadPayPalScript = async () => {
  const vault = isSubscription.value ? '&vault=true&intent=subscription' : '';
  const currencyTag = `&currency=${currency.value}`;
  const desiredKey = `${currencyTag}${vault}`;

  // The SDK is currency/intent-locked at load time and window.paypal persists across
  // modal remounts. If it was loaded with a different currency (e.g. a USD product
  // opened earlier this session), reusing it makes order.create with another currency
  // throw "Unexpected currency". Reload when the required currency/intent differs.
  if ((window.paypal || document.getElementById('paypal-script')) && window.__paypalSdkKey !== desiredKey) {
    unloadPayPalScript();
  }

  if (window.paypal && window.__paypalSdkKey === desiredKey) {
    return;
  }

  if (paypalScriptPromise) {
    return paypalScriptPromise;
  }

  const existingScript = document.getElementById('paypal-script');
  if (existingScript) {
    paypalScriptPromise = new Promise((resolve, reject) => {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
    });
    return paypalScriptPromise;
  }

  const script = document.createElement('script');
  script.id = 'paypal-script';
  script.src = `https://www.paypal.com/sdk/js?client-id=${process.env.VUE_APP_PAYPAL_CLIENT_ID}&disable-funding=credit,card${currencyTag}${vault}`;

  paypalScriptPromise = new Promise((resolve, reject) => {
    script.onload = resolve;
    script.onerror = reject;
  });
  window.__paypalSdkKey = desiredKey;

  document.head.appendChild(script);
  return paypalScriptPromise;
};

const initializePayPalButton = () => {
  if (!window.paypal) {
    console.error('PayPal SDK not available');
    return;
  }

  const host = paypalButtonsHost.value;
  if (!host) {
    console.error('PayPal button host not found');
    return;
  }

  try {
    cleanupPayPalButtons();

    if (isSubscription.value) {
      // Subscription flow
      paypalButtonsInstance.value = window.paypal.Buttons({
        funding: {
          disallowed: [window.paypal.FUNDING.CARD, window.paypal.FUNDING.CREDIT],
        },
        style: {
          shape: 'rect',
          color: 'white',
          layout: 'vertical',
          label: 'subscribe',
          height: 48
        },
        createSubscription: function (data, actions) {
          return actions.subscription.create({
            plan_id: 'P-10W708860D955493EM34NPTY',
            subscriber: {
              name: {
                given_name: store.getters.account.firstName,
                surname: store.getters.account.lastName
              },
              email_address: store.getters.account.email,
              shipping_address: {
                name: {
                  full_name: store.getters.profile.profile.name
                },
                address: {
                  address_line_1: props.contactDetails.lineOne,
                  address_line_2: props.contactDetails.lineTwo || '',
                  admin_area_2: props.contactDetails.city,
                  admin_area_1: props.contactDetails.state || '',
                  postal_code: props.contactDetails.zip,
                  country_code: props.contactDetails.countryCode,
                }
              }
            },
            application_context: {
              brand_name: "Living DNA",
              locale: "en-US",
              shipping_preference: "SET_PROVIDED_ADDRESS",
              user_action: "SUBSCRIBE_NOW",
              payment_method: {
                payer_selected: "PAYPAL",
                payee_preferred: "IMMEDIATE_PAYMENT_REQUIRED"
              }
            }
          });
        },
        onApprove: async function (data, actions) {
          // Prevent multiple processing
          if (processingOrder.value) {
            console.log('Order already being processed');
            return;
          }

          processingOrder.value = true;
          paypalProcessing.value = true;

          // Check if we've already processed this subscription
          if (processedOrderIds.value.has(data.subscriptionID)) {
            alert('This subscription has already been processed. Please check your order history.');
            processingOrder.value = false;
            paypalProcessing.value = false;
            return;
          }

          storeOrderId(data.subscriptionID);

          try {
            if (!primaryVariant.value?.sku) {
              throw new Error('This product is missing ecommerce variant data (SKU).');
            }
            if (!ensureValidShippingSelection()) {
              throw new Error('Invalid shipping selection. Expected shipping slug was not provided.');
            }
            const checkoutShippingSlug = resolveShippingForCheckout();
            if (checkoutShippingSlug === null) {
              throw new Error('Invalid checkout shipping slug. See [Checkout Shipping] logs for details.');
            }
            const details = await actions.subscription.get();

            // Create order in your system
            const order = await OrderService.createPaypalOrder(
              effectiveBarcode.value,
              primaryVariant.value,
              props.contactDetails,
              {},
              checkoutShippingSlug,
              totalPrice.value * 100,
              details.id
            );

            // Generate correlation ID for this payment attempt
            const correlationId = generateCorrelationId();
            console.log('Processing subscription with correlation ID:', correlationId);

            // Confirm the order WITH CORRELATION ID
            const orderStatus = await OrderService.confirmPaypalOrder(order, correlationId);

            // Update local state
            await store.dispatch("SET_ACCOUNT_TESTS");
            await store.dispatch("SET_KIT_TESTS", store.getters.profile.profile.barcode);

            // Close PayPal popups
            closePayPalPopups();

            // Emit events
            emit('order', orderStatus.data);
            emit('closePaymentPaypalModal');

          } catch (error) {
            console.error('Error processing subscription:', error);
            logCheckoutErrorDetails(error, 'paypal-subscription');
            closePayPalPopups();
            emit('close');
            alert('There was an error processing your subscription. Please try again.');
          } finally {
            processingOrder.value = false;
            paypalProcessing.value = false;
          }
        },
        onError: function (err) {
          console.error('PayPal Error:', err);
          processingOrder.value = false;
          paypalProcessing.value = false;
          closePayPalPopups();
          emit('close');
          alert('There was an error with PayPal. Please try another payment method.');
        }
      });
    } else {
      // One-time payment flow
      paypalButtonsInstance.value = window.paypal.Buttons({
        funding: {
          disallowed: [window.paypal.FUNDING.CARD, window.paypal.FUNDING.CREDIT],
        },
        style: {
          layout: 'horizontal',
          color: 'white',
          shape: 'rect',
          label: 'paypal',
          height: 48
        },
        createOrder: function(data, actions) {
          if (!primaryVariant.value?.sku) {
            throw new Error('This product is missing ecommerce variant data (SKU).');
          }

          // FIX: Calculate and format amounts for PayPal
          const formattedTotal = formatPayPalAmount(totalPrice.value);
          const formattedShipping = shippingPrices.value ? formatPayPalAmount(shippingPrices.value) : '0.00';

          // Calculate item total (total - shipping)
          const itemTotalValue = totalPrice.value - (shippingPrices.value || 0);
          const formattedItemTotal = formatPayPalAmount(itemTotalValue);

          console.log('PayPal amounts:', {
            total: formattedTotal,
            shipping: formattedShipping,
            itemTotal: formattedItemTotal
          });

          const purchaseUnits = [{
            amount: {
              currency_code: currency.value,
              value: formattedTotal, // FIX: Use formatted string
              breakdown: {
                item_total: {
                  currency_code: currency.value,
                  value: formattedItemTotal, // FIX: Use formatted string
                }
              },
            }
          }];

          const shippingCountryCode = (props.contactDetails?.countryCode || '').toString().trim().toUpperCase();
          const hasShippingAddress =
            !!props.contactDetails?.lineOne &&
            !!props.contactDetails?.city &&
            !!props.contactDetails?.zip &&
            shippingCountryCode.length === 2;
          const shouldProvideShippingAddress = isShippable.value && hasShippingAddress;

          if (isShippable.value && shippingPrices.value) {
            purchaseUnits[0].amount.breakdown.shipping = {
              currency_code: currency.value,
              value: formattedShipping, // FIX: Use formatted string
            };
          }

          if (shouldProvideShippingAddress) {
            purchaseUnits[0].shipping = {
              name: {
                full_name: `${props.contactDetails?.firstName || ''} ${props.contactDetails?.lastName || ''}`.trim() || store.getters.profile.profile.name
              },
              address: {
                address_line_1: props.contactDetails.lineOne,
                address_line_2: props.contactDetails.lineTwo || '',
                admin_area_2: props.contactDetails.city,
                admin_area_1: props.contactDetails.state || '',
                postal_code: props.contactDetails.zip,
                country_code: shippingCountryCode,
              }
            };
          }

          return actions.order.create({
            intent: "CAPTURE",
            purchase_units: purchaseUnits,
            application_context: {
              brand_name: "Living DNA",
              shipping_preference: isShippable.value
                ? (shouldProvideShippingAddress ? "SET_PROVIDED_ADDRESS" : "GET_FROM_FILE")
                : "NO_SHIPPING"
            }
          });
        },
        onApprove: async function (data, actions) {
          // Prevent multiple processing
          if (processingOrder.value) {
            console.log('Order already being processed');
            return;
          }

          processingOrder.value = true;
          paypalProcessing.value = true;

          // Check if we've already processed this order
          if (processedOrderIds.value.has(data.orderID)) {
            alert('This order has already been processed. Please check your order history.');
            processingOrder.value = false;
            paypalProcessing.value = false;
            return;
          }

          storeOrderId(data.orderID);

          try {
            if (!primaryVariant.value?.sku) {
              throw new Error('This product is missing ecommerce variant data (SKU).');
            }
            if (!ensureValidShippingSelection()) {
              throw new Error('Invalid shipping selection. Expected shipping slug was not provided.');
            }
            const checkoutShippingSlug = resolveShippingForCheckout();
            if (checkoutShippingSlug === null) {
              throw new Error('Invalid checkout shipping slug. See [Checkout Shipping] logs for details.');
            }
            // DO NOT capture here! Just get order details
            const orderDetails = await actions.order.get(data.orderID);
            console.log('PayPal order approved, order details:', orderDetails);
            console.log('PayPal Order ID:', data.orderID);
            console.log('Status:', orderDetails.status);

            // Create order in your system
            const order = await OrderService.createPaypalOrder(
              effectiveBarcode.value,
              primaryVariant.value,
              props.contactDetails,
              {},
              checkoutShippingSlug,
              totalPrice.value * 100,
              data.orderID // Send the order ID to backend
            );

            // Generate correlation ID for this payment attempt
            const correlationId = generateCorrelationId();
            console.log('Processing payment with correlation ID:', correlationId);
            console.log('Sending PayPal Order ID to backend (Ecommerce will capture):', data.orderID);

            // Confirm the order WITH CORRELATION ID
            // Backend (Ecommerce) will handle the PayPal capture
            const orderStatus = await OrderService.confirmPaypalOrder(order, correlationId);

            // Update local state
            await store.dispatch("SET_ACCOUNT_TESTS");
            await store.dispatch("SET_KIT_TESTS", store.getters.profile.profile.barcode);

            // Close PayPal popups
            closePayPalPopups();

            // Emit events
            emit('order', orderStatus.data);
            emit('closePaymentPaypalModal');

          } catch (error) {
            console.error('Error processing payment:', error);
            logCheckoutErrorDetails(error, 'paypal-one-time');
            closePayPalPopups();

            // The ORDER_ALREADY_CAPTURED error should now come from the backend
            // if there's an issue with Ecommerce capturing
            if (error.message?.includes('ORDER_ALREADY_CAPTURED') ||
                error.response?.data?.issue === 'ORDER_ALREADY_CAPTURED') {
              alert('This payment has already been processed. Please check your order history.');
            } else {
              alert('There was an error processing your payment. Please try again.');
            }

            emit('close');
          } finally {
            processingOrder.value = false;
            paypalProcessing.value = false;
          }
        },
        onError: function (err) {
          console.error('PayPal Error:', err);
          processingOrder.value = false;
          paypalProcessing.value = false;
          closePayPalPopups();
          emit('close');
          alert('There was an error with PayPal. Please try another payment method.');
        }
      });
    }
    paypalButtonsInstance.value.render(host);
    paypalButtonRendered.value = true;
  } catch (error) {
    console.error('Error rendering PayPal button:', error);
  }
};

// Lifecycle hooks
onMounted(async () => {
  // Load stored order IDs from localStorage
  processedOrderIds.value = getStoredOrderIds();

  await fetchShippingOptions();
  // Load PayPal script when component is mounted and visible
  if (props.isModalVisible) {
    await loadPayPalScript();
    initializePayPalButton();
  }
});

watch(() => props.isModalVisible, async (visible) => {
  if (visible) {
    await nextTick();
    await loadPayPalScript();
    initializePayPalButton();
  } else {
    // When modal becomes invisible, reset states but don't close PayPal popups
    processingOrder.value = false;
    paypalProcessing.value = false;
    cleanupPayPalButtons();
  }
});

watch(shippingMethods, (newMethods) => {
  if (isShippable.value && newMethods.length > 0 && !selectedShippingMethod.value) {
    if (isKit.value) {
      // Kit products: pick first available API shipping method
      const firstMethod = newMethods[0];
      selectedShippingMethod.value = firstMethod ? shippingValue(firstMethod) : null;
    } else {
      const firstValidSlug = newMethods
        .map((method) => shippingValue(method))
        .find((slug) => EXPECTED_SHIPPING_SLUGS.has(slug));
      selectedShippingMethod.value = firstValidSlug || null;
      if (!firstValidSlug) {
        console.error("[Checkout Shipping] No valid expected shipping slug found in shipping methods.");
        logShippingDebug("No valid expected shipping slug found");
      }
    }
    changeMethod();
  }
  if (isGeneticTest.value && newMethods.length > 0) {
    const hasDigitalCollection = newMethods.some((method) => shippingValue(method) === "digitalcollection");
    if (!hasDigitalCollection) {
      console.error("[Checkout Shipping] Genetic Test missing digitalcollection in Prepr collection_methods.");
      logShippingDebug("Genetic Test missing digitalcollection");
    }
  }
});

watch(() => props.contactDetails, () => {
  fetchShippingOptions();
}, { deep: true });

watch(() => props.product, () => {
  fetchShippingOptions();
}, { deep: true });

onBeforeUnmount(() => {
  cleanupPayPalButtons();
});
</script>

<style scoped>
.payment-container-row {
  padding: 20px;
}

.header-payment-row {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.header-payment-row>div:first-child {
  flex: 0 0 80px;
}

.header-payment-row>div:first-child img {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.header-payment-row>div:nth-child(2) {
  flex: 1;
}

.third-price {
  flex: 0 0 auto;
  text-align: right;
}

.product-text {
  font-weight: bold;
  margin-bottom: 5px;
}

.product-title {
  color: #666;
  font-size: 0.9rem;
  margin: 0;
}

.payment-flex-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.contact-info-row {
  margin: 20px 0;
  padding: 15px;
  border: 1px solid #eee;
  border-radius: 8px;
}

.contact-info-grid-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.contact-info-grid-row p:last-child {
  color: #007bff;
  cursor: pointer;
}

.pricing-container {
  margin: 15px 0;
}

.subcription-price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}

.custom-radio {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.custom-radio input {
  margin-right: 10px;
}

.action-cta {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.wallet-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.wallet-section #pr-button-modal {
  width: 100%;
}

.wallet-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #98a2b3;
  font-size: 13px;
}

.wallet-divider::before,
.wallet-divider::after {
  content: "";
  flex: 1;
  border-bottom: 1px solid #e4e7ec;
}

.wallet-divider span {
  padding: 0 12px;
}

.wallet-error {
  color: #d92d20;
  font-size: 13px;
  margin: 0;
}

#paypal-button-container {
  margin: 10px 0;
}

.alert {
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 15px;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.alert-warning {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  color: #856404;
}

.mt-3 {
  margin-top: 15px;
}

.mt-4 {
  margin-top: 20px;
}

.d-flex {
  display: flex;
}

.pay-by-card {
  width: 100%;
}

/* As wide as the PayPal button under it, not the usual 452px */
.pay-by-card .ldna-btn.ldna-btn {
  max-width: none;
}

/* Phones: the sheet has its own padding, and the summary is a compact row */
@media (max-width: 598px) {
  .payment-container-row {
    padding: 0;
  }

  .payment-container-row .header-payment-row {
    display: grid;
    align-items: center;
    grid-template-columns: 56px minmax(0, 1fr) auto;
    gap: 12px;
    padding: 12px;
    border-radius: 12px;
  }

  .payment-container-row .header-payment-row .product-text {
    font-size: 16px;
    margin-bottom: 4px;
  }

  .payment-container-row .header-payment-row .product-title {
    font-size: 13px;
    line-height: 1.4;
  }
}
</style>
